import { createStore } from "vuex";
import api from "@/api";

export default createStore({
  state: {
    // 機能3: 無効化状態キャッシュ
    // { [id]: { isInvalidated, notFound, moveInId, pinRequired, checkedAt } }
    invalidationMap: {},

    // 機能8: MoveIn ID別の認証トークン
    // { [moveInId]: { token, expiresAt } }
    authMap: {},
  },

  mutations: {
    SET_INVALIDATION(state, { id, result }) {
      const entry = { ...result, checkedAt: new Date().toISOString() };
      const updated = { ...state.invalidationMap, [id]: entry };
      if (result.moveInId && result.moveInId !== id) {
        updated[result.moveInId] = entry;
      }
      state.invalidationMap = updated;
    },

    SET_AUTH(state, { moveInId, token, expiresAt }) {
      state.authMap = {
        ...state.authMap,
        [moveInId]: { token, expiresAt },
      };
    },

    REMOVE_AUTH(state, moveInId) {
      const newMap = { ...state.authMap };
      delete newMap[moveInId];
      state.authMap = newMap;
    },

    CLEAR_INVALIDATION(state, id) {
      const newMap = { ...state.invalidationMap };
      const entry = newMap[id];
      delete newMap[id];
      if (entry && entry.moveInId && entry.moveInId !== id) {
        delete newMap[entry.moveInId];
      }
      state.invalidationMap = newMap;
    },

    CLEANUP_EXPIRED(state) {
      const now = new Date();
      state.authMap = Object.fromEntries(
        Object.entries(state.authMap).filter(
          ([, v]) => new Date(v.expiresAt) > now
        )
      );
    },
  },

  actions: {
    // 機能3: 無効化チェック
    async checkInvalidation({ commit }, { id, type = "movein" }) {
      try {
        const response = await api.get(
          `guide_infomation/check_invalidation?id=${id}&type=${type}`
        );
        commit("SET_INVALIDATION", { id, result: response.data });
        return response.data;
      } catch (error) {
        // 障害時に古い invalidationMap エントリを削除（無効化見逃し防止）
        commit("CLEAR_INVALIDATION", id);
        throw error;
      }
    },

    // 機能8: PIN認証
    async verifyPin({ commit }, { id, pin }) {
      const response = await api.post("guide_infomation/verify_pin", {
        id,
        type: "movein",
        pin,
      });
      if (response.data.verified) {
        commit("SET_AUTH", {
          moveInId: response.data.moveInId,
          token: response.data.token,
          expiresAt: response.data.expiresAt,
        });
        // localStorage に Map 構造で永続化
        const stored = JSON.parse(
          localStorage.getItem("guide_auth") || "{}"
        );
        stored[response.data.moveInId] = {
          token: response.data.token,
          expiresAt: response.data.expiresAt,
        };
        localStorage.setItem("guide_auth", JSON.stringify(stored));
      }
      return response.data;
    },

    // 機能8: ストア初期化時に localStorage から復元（期限切れは除外）
    restoreAuth({ commit }) {
      const stored = JSON.parse(
        localStorage.getItem("guide_auth") || "{}"
      );
      const now = new Date();
      Object.entries(stored).forEach(([moveInId, v]) => {
        if (new Date(v.expiresAt) > now) {
          commit("SET_AUTH", { moveInId, ...v });
        }
      });
      commit("CLEANUP_EXPIRED");
    },
  },

  getters: {
    // 機能3: 無効化状態
    isInvalidated: (state) => (id) => {
      const entry = state.invalidationMap[id];
      return entry ? entry.isInvalidated : false;
    },

    // 機能9: 404判定
    isNotFound: (state) => (id) => {
      const entry = state.invalidationMap[id];
      return entry ? entry.notFound : false;
    },

    // 機能8: PIN認証要否（未確認時は安全側で true）
    isPinRequired: (state) => (id) => {
      const entry = state.invalidationMap[id];
      return entry ? entry.pinRequired : true;
    },

    // 機能2: SG → MoveIn ID 解決
    resolveMoveInId: (state) => (id) => {
      const entry = state.invalidationMap[id];
      return entry && entry.moveInId ? entry.moveInId : id;
    },

    // 機能8: 認証状態チェック
    isAuthenticated: (state) => (moveInId) => {
      const auth = state.authMap[moveInId];
      return !!(auth && auth.token && new Date(auth.expiresAt) > new Date());
    },

    // 機能8: axiosインターセプタ用トークン取得
    getToken: (state) => (moveInId) => {
      const auth = state.authMap[moveInId];
      return auth ? auth.token : null;
    },
  },
});
